import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styles from './ApiReference.module.scss';
import ApiSidebar from './LocalComponents/ApiSidebar';
import ApiDetails from './LocalComponents/ApiDetails';
import RightPanel from './LocalComponents/RightPanel';
import { parseOpenApiSpec } from './utils/api-parser';
import { generatePostmanCollectionFromApiData } from './utils/postman-api-generator';
import { userApi } from '../../../Utils/API';
import { GRAPHQL_PROXY } from '../../../Utils/Endpoints';
import loading from "../../../Assets/Animations/loading.gif";
import WorkflowModal from '../Workflow/LocalComponents/WorkflowModal';
import { useCryptoKey } from '../../../Context/CryptoKey/CryptoKeyContext';
import { loadDecryptedToken } from '../../../Utils/crypto';

const ApiReference = () => {
  // Consume the AES-GCM CryptoKey from context (provided by App.jsx).
  // Used to decrypt the stored api_access_token_enc and prefill the
  // Authorization field when navigating to any API (except Create Access Token).
  const { cryptoKey } = useCryptoKey();

  const [apiData, setApiData] = useState({ all: [], grouped: {}, securitySchemes: {}, baseUrl: '' });
  const [selectedApi, setSelectedApi] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [paramValues, setParamValues] = useState({});
  const [activeScheme, setActiveScheme] = useState(null);
  const [apiSpec, setApiSpec] = useState(null);
  const [workflowMap, setWorkflowMap] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [dummyResponseMap, setDummyResponseMap] = useState(null);
  const [apiDummyResponses, setApiDummyResponses] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(
    localStorage.getItem('sidebarWidth')
      ? parseInt(localStorage.getItem('sidebarWidth'), 10)
      : 320
  );

  const { mainGroupSlug, subGroupSlug, apiSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  async function getApiDummyResponses() {
 const data = `
    {
   aPIDummyResponses {
 flow {
       widgets {
    displayText
      ... on APIResponse {
    displayText
 reason
    statusCode
              responseBody
        }
  }
  }
        endpoint {
      contentItems {
         ... on Endpoints {
displayText
 hTTPMethod {
 termContentItems {
        displayText
         }
       }
   endpointURL
     }
          }
   }
      }
    }
    `;
  const token_type = localStorage.getItem("token_type");
 const access_token = localStorage.getItem("access_token");

    const config = {
      headers: {
 "Content-Type": "application/json",
        Authorization: `${token_type} ${access_token}`,
      },
    };

    const body = { query: data };

    try {
      const resp = await userApi.post(GRAPHQL_PROXY, body, config);
      if (resp.data && resp.data.data && resp.data.data.aPIDummyResponses) {
   setApiDummyResponses(resp.data.data.aPIDummyResponses);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function getApiReferenceData() {
 const data = `
    {
      aPIReferencesPage {
    list {
          contentItems {
          displayText
         ... on APIReference {
        displayText
    markdownBody {
       markdown
   }
            }
     }
        }
      }
    }
    `;
  
    const token_type = localStorage.getItem("token_type");
  const access_token = localStorage.getItem("access_token");

    const config = {
   headers: {
        "Content-Type": "application/json",
        Authorization: `${token_type} ${access_token}`,
      },
    };

  const body = { query: data };
  
    try {
      const resp = await userApi.post(GRAPHQL_PROXY, body, config);
   if (resp.data && resp.data.data && resp.data.data.aPIReferencesPage && resp.data.data.aPIReferencesPage.length > 0) {
 const referenceData = resp.data.data.aPIReferencesPage[0].list.contentItems;
        const newApiSpec = {
  "openapi": "3.0.0",
    "info": {
          "title": "Synnex API",
  "version": "1.0.0"
  },
          "paths": {},
   "components": {
          "schemas": {},
"securitySchemes": {}
      },
       "tags": [],
     "servers": []
        };

 referenceData.forEach(item => {
          if (item.markdownBody && item.markdownBody.markdown) {
    const spec = JSON.parse(item.markdownBody.markdown);
  const serverUrl = spec.servers && spec.servers[0] ? spec.servers[0].url : 'https://api-stage.synnexb2b.com.au/dev';

     // Add main group name to each path's endpoint definition
      for (const path in spec.paths) {
for (const method in spec.paths[path]) {
  spec.paths[path][method]['x-main-group'] = item.displayText;
 spec.paths[path][method]['x-server-url'] = serverUrl;
    }
            }

    // Merge paths, components, etc.
  newApiSpec.paths = { ...newApiSpec.paths, ...spec.paths };
   if (spec.components) {
      if (spec.components.schemas) {
      newApiSpec.components.schemas = { ...newApiSpec.components.schemas, ...spec.components.schemas };
  }
          if (spec.components.securitySchemes) {
           newApiSpec.components.securitySchemes = { ...newApiSpec.components.securitySchemes, ...spec.components.securitySchemes };
     }
   }
    if (spec.tags) {
    newApiSpec.tags = newApiSpec.tags.concat(spec.tags);
  }
     if (spec.servers && spec.servers.length > 0 && newApiSpec.servers.length === 0) {
  newApiSpec.servers = spec.servers;
       }
   }
        });

        setApiSpec(newApiSpec);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function getWorkflowData() {
    const query = `
    query MyQuery {
      workflows(orderBy: {publishedUtc: DESC}) {
        displayText
   list {
    contentItems {
       ... on WorkflowStep {
   stepTitle
    endpoint {
   contentItems {
       displayText
... on Endpoints {
 displayText
     hTTPMethod {
        termContentItems {
   displayText
 }
   }
   endpointURL
        }
 }
            }
    stepDescription {
    html
 }
    codeSnippet
   displayText
      }
      }
        }
    description
      }
    }
    `;

    const token_type = localStorage.getItem("token_type");
    const access_token = localStorage.getItem("access_token");

const config = {
      headers: {
        "Content-Type": "application/json",
     Authorization: `${token_type} ${access_token}`,
      },
    };

    const body = { query };

    try {
const resp = await userApi.post(GRAPHQL_PROXY, body, config);
      if (resp.data && resp.data.data && resp.data.data.workflows) {
    const workflows = resp.data.data.workflows;
  const newWorkflowMap = {};
        workflows.forEach(workflow => {
     const endpoints = [];
      workflow.list.contentItems.forEach(step => {
      if (step.endpoint && step.endpoint.contentItems) {
       step.endpoint.contentItems.forEach(endpointItem => {
  if (endpointItem.endpointURL && endpointItem.hTTPMethod) {
        endpoints.push({
    url: endpointItem.endpointURL,
     method: endpointItem.hTTPMethod.termContentItems[0].displayText
   });
    }
     });
     }
          });
   newWorkflowMap[workflow.displayText] = {
            workflow: workflow,
        endpoints: endpoints
     };
     });
        setWorkflowMap(newWorkflowMap);
  }
    } catch (error) {
      console.error("Error fetching workflow data:", error);
    }
  }

  useEffect(() => {
    getApiReferenceData();
    getWorkflowData();
    getApiDummyResponses();
  }, []);

  useEffect(() => {
    if (apiDummyResponses) {
      const newDummyResponseMap = {};
      apiDummyResponses.forEach(item => {
    if (item.endpoint.contentItems && item.endpoint.contentItems.length > 0) {
       const endpoint = item.endpoint.contentItems[0];
          const method = endpoint.hTTPMethod.termContentItems[0].displayText.replace('Http', '').toUpperCase();
  const key = `${method}${endpoint.endpointURL}`;
          newDummyResponseMap[key] = item.flow.widgets;
        }
      });
      setDummyResponseMap(newDummyResponseMap);
    }
  }, [apiDummyResponses]);

  useEffect(() => {
    if (apiSpec) {
  const { apis: parsedApis, securitySchemes, baseUrl } = parseOpenApiSpec(apiSpec);
      
   const allApis = Object.values(parsedApis).flatMap(mainGroup =>
        Object.values(mainGroup.groups).flatMap(subGroup => subGroup.apis)
      );

      setApiData({ all: allApis, grouped: parsedApis, securitySchemes, baseUrl });
      
      if (securitySchemes) {
   setActiveScheme(Object.keys(securitySchemes)[0]);
    }
    }
  }, [apiSpec]);

  useEffect(() => {
    if (apiData.all.length > 0) {
      if (location.pathname === '/api-reference' || location.pathname === '/api-reference/') {
        const firstMainGroup = Object.values(apiData.grouped)[0];
        if (firstMainGroup && firstMainGroup.slug) {
          const firstSubGroup = Object.values(firstMainGroup.groups)[0];
          if (firstSubGroup && firstSubGroup.slug && firstSubGroup.apis.length > 0) {
  const firstApi = firstSubGroup.apis[0];
   if (firstApi && firstApi.slug) {
     navigate(`/api-reference/${firstMainGroup.slug}/${firstSubGroup.slug}/${firstApi.slug}`, { replace: true });
            }
          }
        }
      } else if (mainGroupSlug && subGroupSlug && apiSlug) {
   const mainGroup = Object.values(apiData.grouped).find(mg => mg.slug === mainGroupSlug);
      if (mainGroup) {
    const subGroup = Object.values(mainGroup.groups).find(sg => sg.slug === subGroupSlug);
  if (subGroup) {
            const foundApi = subGroup.apis.find(a => a.slug === apiSlug);
        setSelectedApi(foundApi);
      } else {
         setSelectedApi(null);
     }
        } else {
   setSelectedApi(null);
  }
      }
 }
  }, [apiData, mainGroupSlug, subGroupSlug, apiSlug, navigate, location.pathname]);
  
  // When the selected API changes, build the initial paramValues.
// For APIs that show the Authorization section (i.e. not Create Access Token),
  // we decrypt the stored api_access_token_enc and pre-fill the auth field.
  // The useEffect uses an inner async IIFE so it can await the decryption.
  useEffect(() => {
    const initParams = async () => {
      if (selectedApi && selectedApi.parameters) {
   const initialValues = {};
        selectedApi.parameters.forEach(p => {
      if (p.schema.type === 'array' && p.schema.items && p.schema.items.properties) {
   initialValues[p.name] = [{}];
          }
        });

        // Pre-fill the Authorization field from the AES-GCM encrypted stored token.
        // Only for APIs other than the Create Access Token endpoint itself.
      // If the CryptoKey is not yet ready (CMS fetch pending/failed), decryption
  // returns null and the field is simply left empty — no error is thrown.
  const isCreateTokenApi = selectedApi.path === '/api/Security/GetAccessToken';
        if (!isCreateTokenApi) {
          const storedToken = await loadDecryptedToken(cryptoKey);
          if (storedToken && apiData.securitySchemes) {
    const schemeKey = Object.keys(apiData.securitySchemes)[0];
 if (schemeKey) {
              const schemeName = apiData.securitySchemes[schemeKey].name;
           initialValues[schemeName] = storedToken;
       }
          }
  }

        setParamValues(initialValues);
      } else {
        setParamValues({});
      }
    };

 initParams();
  }, [selectedApi, apiData.securitySchemes, cryptoKey]);

  const handleApiSelect = (mainGroupSlug, subGroupSlug, apiSlug) => {
    navigate(`/api-reference/${mainGroupSlug}/${subGroupSlug}/${apiSlug}`);
  };

  const handleWorkflowClick = (workflow, icon) => {
  setSelectedWorkflow(workflow);
    setSelectedIcon(icon);
  setIsModalOpen(true);
  };

  const closeModal = () => {
 setIsModalOpen(false);
    setSelectedWorkflow(null);
    setSelectedIcon(null);
  };

  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add(styles.modalOpen);
    } else {
      document.body.classList.remove(styles.modalOpen);
    }
 return () => {
      document.body.classList.remove(styles.modalOpen);
    };
  }, [isModalOpen]);

  const handleDownloadApiCollection = () => {
    const collection = generatePostmanCollectionFromApiData(apiData, "Synnex API Collection");
    const blob = new Blob([JSON.stringify(collection, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
    a.href = url;
    a.download = `Synnex-API-Collection.postman_collection.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleParamChange = (action, payload) => {
    setParamValues(prev => {
   const newValues = { ...prev };
   switch (action) {
     case 'ADD_ARRAY_ITEM': {
     const { paramName } = payload;
     if (!newValues[paramName]) newValues[paramName] = [];
 newValues[paramName] = [...newValues[paramName], {}];
 return newValues;
  }
        case 'REMOVE_ARRAY_ITEM': {
          const { paramName, index } = payload;
     newValues[paramName] = newValues[paramName].filter((_, i) => i !== index);
          return newValues;
     }
     case 'UPDATE_ARRAY_ITEM': {
     const { paramName, index, field, value } = payload;
          const newArray = [...(newValues[paramName] || [])];
    newArray[index] = { ...newArray[index], [field]: value };
        newValues[paramName] = newArray;
return { ...newValues };
    }
        default:
        newValues[action] = payload;
   return newValues;
    }
    });
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = useCallback((e) => {
    const minWidth = 280;
    const maxWidth = 600;
  let newWidth = e.clientX;
    if (newWidth < minWidth) newWidth = minWidth;
    if (newWidth > maxWidth) newWidth = maxWidth;
    setSidebarWidth(newWidth);
  }, []);

  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  useEffect(() => {
    localStorage.setItem('sidebarWidth', sidebarWidth);
  }, [sidebarWidth]);

  if (!apiData.all.length) {
    return (
      <div className={styles.loadingContainer}>
<img src={loading} alt="loading..." />
  </div>
    );
  }

const showAuthorization = selectedApi?.path !== '/api/Security/GetAccessToken';

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  
  return (
    <div
      className={styles.apiReferenceContainer}
style={{ '--sidebar-width': `${sidebarWidth}px` }}
    >
      <button className={styles.hamburger} onClick={toggleSidebar}>
 <i className="material-icons">segment</i>
    </button>
  {isSidebarOpen && <div className={styles.overlay} onClick={toggleSidebar}></div>}
      <div className={styles.leftPane} style={{ width: sidebarWidth }}>
        <ApiSidebar
     apiGroups={apiData.grouped}
     onApiSelect={handleApiSelect}
 selectedApi={selectedApi}
  searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
   onDownload={handleDownloadApiCollection}
isOpen={isSidebarOpen}
   onClose={toggleSidebar}
        />
        <div className={styles.resizer} onMouseDown={handleMouseDown}></div>
      </div>
      <div className={styles.centerPane}>
    <ApiDetails
          api={selectedApi}
      paramValues={paramValues}
   handleParamChange={handleParamChange}
      workflowMap={workflowMap}
         onWorkflowClick={handleWorkflowClick}
       />
  </div>
     <div className={styles.rightPane}>
       <RightPanel
      api={selectedApi}
       baseUrl={apiData.baseUrl}
     paramValues={paramValues}
      handleParamChange={handleParamChange}
       securitySchemes={apiData.securitySchemes}
      activeScheme={activeScheme}
      setActiveScheme={setActiveScheme}
      dummyResponseMap={dummyResponseMap}
  showAuthorization={showAuthorization}
       />
     </div>
     {isModalOpen && (
    <WorkflowModal
         workflow={selectedWorkflow}
         icon={selectedIcon}
    onClose={closeModal}
   />
     )}
   </div>
  );
};

export default ApiReference;
